import { useState } from 'react';
import { Alert, Button, Input, Select } from '@piggytechng/component-library';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import dayjs from 'dayjs';
import { FaCalendarAlt } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { formatToNaira, OverideButtonStyle, OverideInputStyle, OverideSelectStyle } from '../../../../helpers/utils';
import usePiggybank from '../../../../hooks/usePiggybank';
import useRolePermissions from '../../../../hooks/useRolePermissions';
import ModalExecuteAction from '../../../../components/Modals/ModalExecuteAction';
import { RootState } from '../../../../store';
import { setModalActions } from '../../../../store/slices/utils';
import { PiggybankWithdrawalDays } from '../../../../types';

const CUSTOM_DAY = 'custom';

// The four PiggyVest free withdrawal days; value is "month-day"
const piggyvestDays = [
  { label: 'Every 31st of March', value: '3-31' },
  { label: 'Every 30th of June', value: '6-30' },
  { label: 'Every 30th of September', value: '9-30' },
  { label: 'Every 31st of December', value: '12-31' },
  { label: 'Custom date', value: CUSTOM_DAY },
];

const WITHDRAWAL_DAY_MEMO_MAX = 500;

const formatUnix = (ts?: number | null) => (ts ? dayjs.unix(ts).format('D MMM YYYY') : 'None');

// Sets / resets the PiggyBank free withdrawal day. Lives in its own component so the piggybank queries only mount
// while the Withdrawal Day sub-tab is open.
function WithdrawalDay({ userId, restrictActionByRole }: any) {
  const style = { ...OverideSelectStyle, style: { ...OverideSelectStyle.style, zIndex: undefined } };
  const { piggybankWalletDataQ, setWithdrawalDay, resetWithdrawalDay, isRunningWithdrawalDayAction } = usePiggybank();
  const { isSuperAdmin } = useRolePermissions({});
  const modalActions = useSelector((state: RootState) => state.modalActions);
  const dispatch = useDispatch();

  const [selectedDay, setSelectedDay] = useState('');
  const [customDate, setCustomDate] = useState<Date | null>(null);
  const [memo, setMemo] = useState('');
  const [force, setForce] = useState(false);
  const [showSetModal, setShowSetModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [ruleBlock, setRuleBlock] = useState<{ earliestAllowed?: number; anchoredOn?: number } | null>(null);

  const withdrawalDays: PiggybankWithdrawalDays | undefined = piggybankWalletDataQ?.withdrawalDays;
  const customDay = withdrawalDays?.customDay;
  const isCustomDay = !!customDay && customDay.active && withdrawalDays?.isDefault === false;

  const monthDay = (): { month: number; day: number } | null => {
    if (selectedDay === CUSTOM_DAY) {
      if (!customDate) return null;
      return { month: customDate.getMonth() + 1, day: customDate.getDate() };
    }
    if (!selectedDay) return null;
    const [month, day] = selectedDay.split('-').map(Number);
    return { month, day };
  };

  const chosen = monthDay();
  const chosenLabel = chosen ? dayjs(new Date(2000, chosen.month - 1, chosen.day)).format('D MMM') : '';

  const resetForm = () => {
    setSelectedDay('');
    setCustomDate(null);
    setMemo('');
    setForce(false);
    setShowSetModal(false);
    setShowResetModal(false);
  };

  const closeModal = (setter: (state: boolean) => void) => (state: boolean) => {
    setter(state);
    if (!state) dispatch(setModalActions({ ...modalActions, password: '' }));
  };

  const openSetConfirmation = () => {
    if (restrictActionByRole('flagAndActions', 'withdrawalDay')) return;
    if (!isSuperAdmin) {
      Alert.warn('You do not have access to perform this action.');
      return;
    }
    if (!chosen) {
      Alert.error(selectedDay === CUSTOM_DAY ? 'Pick the custom date.' : 'Choose a withdrawal day.');
      return;
    }
    if (!memo.trim()) {
      Alert.error('Kindly enter a memo for this change.');
      return;
    }
    setShowSetModal(true);
  };

  const openResetConfirmation = () => {
    if (restrictActionByRole('flagAndActions', 'withdrawalDay')) return;
    if (!isSuperAdmin) {
      Alert.warn('You do not have access to perform this action.');
      return;
    }
    if (!isCustomDay) {
      Alert.error('This customer is already on PiggyVest withdrawal days.');
      return;
    }
    if (!memo.trim()) {
      Alert.error('Kindly enter a memo for the reset.');
      return;
    }
    setShowResetModal(true);
  };

  const handleSet = async (password: string) => {
    if (!chosen) return;
    setRuleBlock(null);
    const result = await setWithdrawalDay(
      userId,
      { month: chosen.month, day: chosen.day, memo: memo.trim(), force },
      password,
      () => resetForm(),
    );
    if (result && result.status === false && result.data?.earliestAllowed) {
      setRuleBlock(result.data);
      setShowSetModal(false);
    }
  };

  const handleIconClick = () => {
    const input = document.querySelector('.withdrawal-day .react-datepicker__input-container input');
    if (input) (input as HTMLElement).click();
  };

  return (
    <>
      <p className={`withdrawal-day__current ${isCustomDay ? 'custom' : ''}`}>
        {withdrawalDays ? (
          isCustomDay ? (
            <>
              Current setting: Custom {dayjs(new Date(2000, customDay.month - 1, customDay.day)).format('D MMM')}
              {customDay.setAt ? ` (set ${formatUnix(customDay.setAt)})` : ''}
              {customDay.memo ? ` | ${customDay.memo}` : ''}. Next free withdrawal day:{' '}
              {formatUnix(withdrawalDays.nextWithdrawalDay)}.
            </>
          ) : (
            <>
              Current setting: PiggyVest days (default). Next free withdrawal day:{' '}
              {formatUnix(withdrawalDays.nextWithdrawalDay)}.
            </>
          )
        ) : (
          'Loading current withdrawal day...'
        )}
      </p>

      {ruleBlock && (
        <p className="withdrawal-day__rule-block">
          Blocked by the 90-day rule: the earliest allowed date is {formatUnix(ruleBlock.earliestAllowed)}
          {ruleBlock.anchoredOn ? ` (counted from ${formatUnix(ruleBlock.anchoredOn)})` : ''}. Tick &quot;Override
          90-day rule&quot; to set it anyway.
        </p>
      )}

      <Select
        label="Select Day"
        {...style}
        labelStyle={{ color: '#666766' }}
        backgroundColor="#F9FAFB"
        outlineColor="none"
        height="60px"
        placeholder="Choose Day"
        value={selectedDay}
        options={piggyvestDays}
        onChange={(option: any) => {
          setSelectedDay(option ?? '');
          if (option !== CUSTOM_DAY) setCustomDate(null);
        }}
      />

      {selectedDay === CUSTOM_DAY && (
        <div className="withdrawal-day__date input-cont flex--4">
          <label htmlFor="customWithdrawalDate">Custom date (only the month and day are kept)</label>
          <DatePicker
            selected={customDate}
            name="customWithdrawalDate"
            onChange={(date) => setCustomDate(date)}
            className="date-picker"
            placeholderText="Select the free withdrawal date"
            dateFormat="dd MMMM, yyyy"
          />
          <FaCalendarAlt onClick={handleIconClick} className="calendar" color="#212337" />
        </div>
      )}

      <div className="reason-field">
        <p>Memo</p>
        <textarea
          name="withdrawal-day-memo"
          placeholder="Why is the withdrawal day being changed?"
          rows={4}
          cols={50}
          maxLength={WITHDRAWAL_DAY_MEMO_MAX}
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
        />
        <small className="reason-field__count">
          {memo.length}/{WITHDRAWAL_DAY_MEMO_MAX}
        </small>
      </div>

      <label className="withdrawal-day__force">
        <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
        <span>Override 90-day rule</span>
      </label>

      <Button
        type="submit"
        {...OverideButtonStyle}
        style={{ fontFamily: 'Jakarta' }}
        onClick={openSetConfirmation}
        loading={isRunningWithdrawalDayAction}
        disabled={!chosen || !memo.trim()}
      >
        Set Day
      </Button>

      <div className="withdrawal-day__reset">
        <p>Remove the custom day and put the customer back on the four PiggyVest withdrawal days.</p>
        <Button
          type="submit"
          {...OverideButtonStyle}
          style={{ fontFamily: 'Jakarta', color: '#212337' }}
          color="#F7F7F7"
          onClick={openResetConfirmation}
          disabled={isRunningWithdrawalDayAction || !isCustomDay}
        >
          Use PiggyVest days (reset)
        </Button>
      </div>

      <ModalExecuteAction
        modalIsOpen={showSetModal}
        setIsOpen={closeModal(setShowSetModal)}
        action={(password: string) => handleSet(password)}
        text={`set this customer's free withdrawal day to ${chosenLabel}${force ? ' (overriding the 90-day rule)' : ''}?`}
        actionColor={'#1734ca'}
        isLoading={isRunningWithdrawalDayAction}
      />

      <ModalExecuteAction
        modalIsOpen={showResetModal}
        setIsOpen={closeModal(setShowResetModal)}
        action={(password: string) => resetWithdrawalDay(userId, memo.trim(), password, () => resetForm())}
        text="put this customer back on PiggyVest withdrawal days?"
        actionColor={'#1734ca'}
        isLoading={isRunningWithdrawalDayAction}
      />
    </>
  );
}

export default function AutosaveAndWithdrawal({
  context,
  restrictActionByRole,
  userId,
  setWithdrawalDelay,
  isRunningUserAction,
}: any) {
  const style = { ...OverideSelectStyle, style: { ...OverideSelectStyle.style, zIndex: undefined } };
  const inputStyle = { ...OverideInputStyle, labelStyle: { ...OverideInputStyle.labelStyle, lineHeight: '23px' } };

  const [selectedTime, setSelectedTime] = useState('');
  const [autosaveAmount, setAutosaveAmount] = useState('');
  const [autosaveFrequency, setAutosaveFrequency] = useState('');
  const [showAutosaveModal, setShowAutosaveModal] = useState(false);

  const { setupAutosave, isRunningAutosaveAction } = usePiggybank();
  const modalActions = useSelector((state: RootState) => state.modalActions);
  const dispatch = useDispatch();

  const amountValue = Number(autosaveAmount);
  const canSetAutosave = amountValue > 0 && !!autosaveFrequency;

  const resetAutosaveForm = () => {
    setAutosaveAmount('');
    setAutosaveFrequency('');
    setShowAutosaveModal(false);
  };

  const closeAutosaveModal = (state: boolean) => {
    setShowAutosaveModal(state);
    if (!state) dispatch(setModalActions({ ...modalActions, password: '' }));
  };

  const openAutosaveConfirmation = () => {
    if (restrictActionByRole('flagAndActions', 'setAutosave')) return;
    if (!amountValue || amountValue <= 0) {
      Alert.error('Kindly enter a valid autosave amount.');
      return;
    }
    if (!autosaveFrequency) {
      Alert.error('Kindly choose an autosave frequency.');
      return;
    }
    setShowAutosaveModal(true);
  };

  const time = [
    { label: '1 Hour', value: '1' },
    { label: '2 Hours', value: '2' },
    { label: '6 Hours', value: '6' },
    { label: '12 Hours', value: '12' },
    { label: '24 Hours', value: '24' },
  ];

  const handleSaveWithdrawal = (action: string) => {
    if (restrictActionByRole('flagAndActions', 'withdrawalDelay')) return;

    if (action === 'withdrawal-delay') {
      if (!selectedTime) return;
      setWithdrawalDelay(userId, Number(selectedTime));
    }
  };

  return (
    <div className={context}>
      {context === 'autosave' ? (
        <>
          <Input
            {...inputStyle}
            label="Amount"
            className="user-form-input"
            height="60px"
            placeholder="Enter amount"
            backgroundColor="#F9FAFB"
            outlineColor="transparent"
            type="text"
            value={autosaveAmount}
            onChange={(e: any) => setAutosaveAmount(e.target.value.replace(/[^\d.]/g, ''))}
          />

          <Select
            label="Frequency"
            {...style}
            labelStyle={{ color: '#666766' }}
            backgroundColor="#F9FAFB"
            outlineColor="none"
            height="60px"
            placeholder="Choose Frequency"
            value={autosaveFrequency}
            options={[
              { value: 'daily', label: 'Daily' },
              { value: 'weekly', label: 'Weekly' },
              { value: 'monthly', label: 'Monthly' },
            ]}
            onChange={(option: any) => setAutosaveFrequency(option ?? '')}
          />

          <Button
            type="submit"
            {...OverideButtonStyle}
            style={{ fontFamily: 'Jakarta', color: '#212337' }}
            color="#F7F7F7"
            onClick={resetAutosaveForm}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            {...OverideButtonStyle}
            style={{ fontFamily: 'Jakarta' }}
            onClick={openAutosaveConfirmation}
            loading={isRunningAutosaveAction}
            disabled={!canSetAutosave}
          >
            Set Autosave
          </Button>

          <ModalExecuteAction
            modalIsOpen={showAutosaveModal}
            setIsOpen={closeAutosaveModal}
            action={(password: string) =>
              setupAutosave(userId, { amount: amountValue, frequency: autosaveFrequency }, password, () =>
                resetAutosaveForm(),
              )
            }
            text={`set autosave of ${formatToNaira(amountValue)} ${autosaveFrequency} for this customer?`}
            actionColor={'#1734ca'}
            isLoading={isRunningAutosaveAction}
          />
        </>
      ) : context === 'withdrawal-day' ? (
        <WithdrawalDay userId={userId} restrictActionByRole={restrictActionByRole} />
      ) : (
        <>
          <Select
            label="Select Time"
            {...style}
            labelStyle={{ color: '#666766' }}
            backgroundColor="#F9FAFB"
            outlineColor="none"
            height="60px"
            placeholder="Choose Time"
            value={selectedTime}
            options={time}
            onChange={(option: any) => setSelectedTime(option ?? '')}
          />

          <Button
            type="submit"
            {...OverideButtonStyle}
            style={{ fontFamily: 'Jakarta' }}
            onClick={() => handleSaveWithdrawal(context)}
            loading={isRunningUserAction}
            disabled={!selectedTime}
          >
            Set Time
          </Button>
        </>
      )}
    </div>
  );
}
